import HLS from 'hls-parser';
import EventEmitter from '../../common/EventEmitter';
import { InternalEvents } from '../../events';
import Counter from '../utils/Counter';
import logger from '../../log';

const log = logger.getLogger(__filename);

type MasterPlaylist = HLS.types.MasterPlaylist;
type MediaPlaylist = HLS.types.MediaPlaylist;

const eventEmitter = EventEmitter.getInstance();

interface Segment {
  uri: string;
  mediaSequenceNumber?: number;
}

interface MediaPlaylistData {
  uri: string;
  segments: Segment[];
}

export interface Variant {
  uri: string;
  bandwidth: number;
  bwordinal?: number;
  resolution: { height: number; width: number };
}

interface MasterPlaylistData {
  uri: string;
  broadcastId: string;
  variants: Variant[];
}

/**
 * A class responsible for intercepting playlist events, and coalescing the relevant data needed to map urls to object ids for Amazon IVS streams
 *
 * An IVS Object Id consists of a Broadcast Id, Bandwidth for the variant, Resolution for the variant, and the Media Sequence Number of the segment.
 */
export class IvsHlsOidMap {
  handledMaster = false;
  masterUri: string;
  broadcastId: string;
  variants: Map<string, Variant>;
  counters: Map<string, Counter>;
  oids: Map<string, string>;

  constructor() {
    log.debug('Started IVS mapper');
    // these get initialized when master playlist is handled
    this.masterUri = 'UNINIT';
    this.broadcastId = 'UNINIT';

    this.variants = new Map();
    this.counters = new Map();
    this.oids = new Map();
    eventEmitter.addEventListener(
      InternalEvents.MASTER_PLAYLIST_OID_DATA,
      evt => {
        const data = evt.data as MasterPlaylistData;
        this.handleMasterPlaylist(data);
      }
    );
    eventEmitter.addEventListener(
      InternalEvents.MEDIA_PLAYLIST_OID_DATA,
      evt => {
        const data = evt.data as MediaPlaylistData;
        this.handleMediaPlaylist(data);
      }
    );
  }

  private handleMasterPlaylist(data: MasterPlaylistData) {
    this.handledMaster = true;
    this.broadcastId = data.broadcastId;
    setVariantsBandwidthOrdinals(data.variants);
    log.debug('IVS: MASTER_PLAYLIST; data:', data, 'variants:', this.variants);
    // set variants
    data.variants.forEach(v => {
      this.variants.set(v.uri, v);
    });
  }

  private getCounter(uri: string): Counter {
    let counter = this.counters.get(uri);
    if (!counter) {
      counter = new Counter();
      this.counters.set(uri, counter);
    }
    return counter;
  }

  private handleMediaPlaylist(data: MediaPlaylistData) {
    const variant = this.variants.get(data.uri);
    const { handledMaster } = this;
    log.debug('IVS: MEDIA_PLAYLIST:', data, { handledMaster, variant });
    if (!this.handledMaster) {
      return;
    }

    data.segments.forEach(({ uri: segmentUri, mediaSequenceNumber }) => {
      // TODO - handle the fact that the uri might just be a path
      if (!mediaSequenceNumber) {
        const counter = this.getCounter(data.uri);
        mediaSequenceNumber = counter.get();
        counter.increment();
      }
      // TODO - insert segments into map
      // capture data from the masterplaylist and merge
      // into a oid with the segment uri being the lookup
      if (!variant) {
        // if we can't find the variant, we messed up
        return;
      }
      const bwordinal = variant.bwordinal;
      const resolution = variant.resolution;
      const oid = `${this.broadcastId}-${bwordinal}-${resolution.width}X${resolution.height}-${mediaSequenceNumber}`;
      this.oids.set(segmentUri, oid);
    });
  }

  getOid(uri: string): string | undefined {
    if (!this.handledMaster) {
      return undefined;
    }
    return this.oids.get(uri);
  }
}

/**
 * This function determines the BandwidthOrdinal for the variants.
 *
 * Because of how IVS works the bandwidth in the file can change between
 * viewers. Even someone loading the playlist a second later could get
 * different bandwidths. So we need to determine the bandwidth based on the
 * resolution and the bandwidth size in relation to the other variants with the
 * same resolution.
 *
 * See: https://kollective.atlassian.net/browse/SRV-4201
 *
 * @param variants - the list of different variants, they can have the same
 *    resolution or same bandwidth as another variant but not both, the
 *    variants will be changed in place adding the bwordinal key to the variant
 *    which will be picked up by the functionality that creates the oids. This
 *    allows us to have an unchanging oid even though the bandwidth will change
 *    over time.
 */
export const setVariantsBandwidthOrdinals = (variants: Variant[]): void => {
  const mapped = variants.reduce((acc, current) => {
    const {
      bandwidth,
      resolution: { width, height }
    } = current;
    const key = `${width}X${height}`;
    if (!acc.has(key)) {
      acc.set(key, new Map());
    }
    (acc.get(key) as Map<number, Variant>).set(bandwidth, current);
    return acc;
  }, new Map() as Map<string, Map<number, Variant>>);
  [...mapped.values()].forEach(bwVariantMap => {
    const keys = [...bwVariantMap.keys()];
    keys.sort((a, b) => a - b);
    const counter = new Counter();
    keys.forEach(key => {
      (bwVariantMap.get(key) as Variant).bwordinal = counter.getAndIncrement();
    });
  });
};

/**
 * This function emits events with the relevant data needed for interacting with manifests.
 *
 * @param uri - the uri the manifest was requested from
 * @param playlist - the playlist object as parsed by hls-parser
 */
export function monitorManifest(
  uri: string,
  playlist: MasterPlaylist | MediaPlaylist
): void {
  log.debug('monitorManifest: isMasterPlaylist:', playlist.isMasterPlaylist);
  if (playlist.isMasterPlaylist) {
    // handle master playlist data
    const master = playlist as MasterPlaylist;
    const broadcastId = master.sessionDataList.find(
      sd => sd.id === 'BROADCAST-ID'
    )?.value;
    if (!broadcastId) {
      // the lack of a broadcastId means we can't complete. bail.
      return;
    }

    // only take necessary values
    const variants = master.variants
      // all three values should be defined. Filter variants with undefined requirements.
      // using reduce as filter then map doesn't get the types right in typescript.
      .reduce((acc, { uri, bandwidth, resolution }) => {
        if (uri && bandwidth && resolution) {
          acc.push({
            uri,
            bandwidth,
            resolution
          });
        }
        return acc;
      }, [] as Variant[]);

    if (variants && variants.length < 1) {
      // no valid variants, no need to move forward.
      return;
    }

    // explicit type declaration as the event doesn't take the data type into account
    const data: MasterPlaylistData = { uri, broadcastId, variants };

    eventEmitter.emit(InternalEvents.MASTER_PLAYLIST_OID_DATA, {
      data
    });
  } else {
    // handle media playlist data
    const media = playlist as MediaPlaylist;
    const segments = media.segments.reduce(
      (acc, { uri, mediaSequenceNumber }) => {
        acc.push({ uri, mediaSequenceNumber });
        return acc;
      },
      [] as Segment[]
    );

    const data: MediaPlaylistData = { uri, segments };
    eventEmitter.emit(InternalEvents.MEDIA_PLAYLIST_OID_DATA, {
      data
    });
  }
}
